import React from 'react';
import styled from 'styled-components';
import throttle from 'lodash/throttle';
import MediaQuery from 'react-responsive';
import { isIOS, isIOS13, isMobileSafari, isIPad13 } from 'react-device-detect';

import DesktopTable from 'components/ui/DesktopTable';
import Tabs from 'components/ui/Tabs';
import { getTranslatedString } from 'helpers/i18n';
import Icon, { LoadingOutlined } from '@ant-design/icons';

const StyledTabbedTableContainer = styled.div`
  width: 100%;
  padding-top: 5px;
  position: relative;
  .ant-tabs-tab-prev.ant-tabs-tab-arrow-show,
  .ant-tabs-tab-next.ant-tabs-tab-arrow-show {
    height: calc(100% - 20px);
    margin-top: 20px;
  }
`;

export function TabbedTable({
  tabs,
  columns,
  onRowClick,
  groupRowsBy,
  data,
  searchResultsCount,
  loadMoreData,
  isLoading,
  height,
  setRef,
  filtersActive,
  onColumnClick,
  searchTotalResultsCount,
  emptyTableText,
}) {
  const [offsetTop, setOffsetTop] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);
  const tabsEl = React.useRef(null);

  // Filter out any hidden tabs we don't want the user to see
  tabs = tabs.filter((tab) => !tab.hidden);

  React.useEffect(() => {
    if (!height && !offsetTop) {
      const rect = tabsEl.current.getBoundingClientRect();
      setOffsetTop(rect.top + rect.height + 40);
    }
  }, [height, offsetTop]);

  const handleTabChange = React.useCallback(
    throttle(
      (activeKey) => {
        setActiveTab(activeKey);
        tabs[parseInt(activeKey, 10)].onClick();
      },
      300,
      { trailing: true, leading: true }
    ),
    [setActiveTab, tabs]
  );

  const availableTabs = () => {
    if (filtersActive) {
      return [
        {
          title: getTranslatedString('landlordPortal.tabbedTable.searchResults'),
          count: searchTotalResultsCount || data.length, // INFO: backwards support for tables that don't return total results
          isSearchResultsTab: true,
        },
      ];
    }
    return tabs;
  };

  const handlePageWidthChange = (tabsEl, offsetTop) => {
    if (!tabsEl.current) return;
    const rect = tabsEl.current.getBoundingClientRect();
    if (offsetTop === rect.top + rect.height) return;
    setOffsetTop(rect.top + rect.height);
  };

  const mobileBrowserOffset = isIOS13 || isIOS || isIPad13 || isMobileSafari ? 100 : 0;

  return (
    <StyledTabbedTableContainer>
      <MediaQuery query="(max-device-width: 750px)">
        {handlePageWidthChange(tabsEl, offsetTop)}
      </MediaQuery>
      <div ref={tabsEl} id="jest-tabs">
        <Tabs onChange={handleTabChange} activeKey={`${activeTab}`}>
          {availableTabs().map((tab, i) => {
            if (isLoading && tab.isSearchResultsTab) {
              return (
                <Tabs.TabPane
                  tab={
                    <span>
                      {' '}
                      {tab.title} <Icon component={LoadingOutlined} spin />
                    </span>
                  }
                  key={i}
                />
              );
            }
            return <Tabs.TabPane tab={`${tab.title} (${tab.count})`} key={i} />;
          })}
        </Tabs>
      </div>
      {height || offsetTop ? (
        <DesktopTable
          data={data}
          setRef={setRef}
          loadMoreData={loadMoreData}
          columns={columns.filter((column) => !column.hide)}
          height={height || `calc(100vh - ${offsetTop + mobileBrowserOffset}px)`}
          isLoading={isLoading}
          onRowClick={onRowClick}
          groupRowsBy={groupRowsBy}
          onColumnClick={onColumnClick}
          emptyTableText={emptyTableText}
        />
      ) : null}
    </StyledTabbedTableContainer>
  );
}
